// 028B — Atención Niño-Niña de 2 Meses a 5 Años (Serie 028, Fase 2, ver
// PLAN.md / tasks/investigacion-028-aiepi-denver.md). El título/contenido
// del código anterior estaba mal puesto (decía "< 2 Meses", duplicando el
// rango de 028A) — se reconstruye con el rango y contenido reales: una sola
// atención por página (sin el formato de 2 consultas de 028A/028C), AIEPI
// de 21 ítems (más rico que 028A) + examen físico de 17 sistemas/regiones +
// hasta 8 diagnósticos con marca PRE/DEF. Pasa a Patrón B, mismo criterio
// que 028A/038/033.
//
// Fuente primaria: páginas 124-125 del PDF oficial AM-00115-2021.
// "Datos del profesional responsable" (bloque M) no se captura acá: nombre,
// matrícula y firma los arma el servidor al firmar.
//
// Único contenido con recursos FHIR reales: cada diagnóstico no vacío crea
// un `Condition` — mismo patrón que 028A/038.
import type { JSX } from 'react';
import { useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { FormularioDiscretoProps, Par } from './index';
import { AreaDictado } from './AreaDictado';
import { CampoDictado } from './CampoDictado';
import { ChecklistGrid } from './ChecklistGrid';
import { codeDeDiagnostico, diagnosticoConMarcaVacio, SelectorDiagnosticoConMarca, textoDiagnosticoConMarca, type DiagnosticoConMarca, type OpcionMarca } from './MarcaDiagnostico';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { EXAMEN_FISICO_028B } from './bloques028Datos';

const CODIGO = '028B';
const TOPE_DIAGNOSTICOS = 8;

const AIEPI_2M_5A = [
  'signosPeligroGeneral',
  'tosDificultadRespiratoria',
  'diarrea',
  'fiebre',
  'problemaOido',
  'problemaGarganta',
  'desnutricionAguda',
  'sobrepesoObesidad',
  'tallaBaja',
  'tendenciaCrecimientoApropiada',
  'anemia',
  'requiereVitaminaA',
  'requiereMicronutrientes',
  'requiereHierro',
  'requiereAlbendazol',
  'otrosProblemas',
  'maltratoInfantil',
  'problemasDesarrolloPsicomotor',
  'faltaVacunas',
  'problemasSaludOral',
  'riesgoSocial',
] as const;

const EXAMEN_FISICO_SISTEMAS = EXAMEN_FISICO_028B;

const TIPO_CONSULTA_OPCIONES = ['primera', 'reEvaluacion'] as const;

interface ConstantesVitales {
  temperatura: string;
  axilar: string;
  bucal: string;
  digital: string;
  pulso: string;
  frecuenciaRespiratoria: string;
  pulsioximetria: string;
  perimetroCefalico: string;
  peso: string;
  longitudTalla: string;
  tensionArterial: string;
}
const CONSTANTES_VACIAS: ConstantesVitales = {
  temperatura: '',
  axilar: '',
  bucal: '',
  digital: '',
  pulso: '',
  frecuenciaRespiratoria: '',
  pulsioximetria: '',
  perimetroCefalico: '',
  peso: '',
  longitudTalla: '',
  tensionArterial: '',
};

function CampoTexto({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={label} etiqueta={label} valor={value} onChange={onChange} />;
}

export function Formulario028B({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f028b.titulo');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo);

  const [constantes, setConstantes] = useState<ConstantesVitales>(CONSTANTES_VACIAS);
  const [informante, setInformante] = useState('');
  const [tipoConsulta, setTipoConsulta] = useState('');
  const [motivoConsultaTexto, setMotivoConsultaTexto] = useState('');
  const [enfermedadActual, setEnfermedadActual] = useState('');
  const [antecedentesPersonales, setAntecedentesPersonales] = useState('');
  const [antecedentesFamiliares, setAntecedentesFamiliares] = useState('');
  const [aiepi, setAiepi] = useState<Record<string, boolean>>({});
  const [examenFisico, setExamenFisico] = useState<Record<string, boolean>>({});
  const [descripcionExamenFisico, setDescripcionExamenFisico] = useState('');
  const [analisisCaso, setAnalisisCaso] = useState('');
  const [clasificacionAiepi, setClasificacionAiepi] = useState('');
  const [diagnosticos, setDiagnosticos] = useState<DiagnosticoConMarca[]>(() => Array.from({ length: TOPE_DIAGNOSTICOS }, () => diagnosticoConMarcaVacio()));
  const [planTratamiento, setPlanTratamiento] = useState('');
  const [proximaCitaIdeal, setProximaCitaIdeal] = useState('');

  const marcasDiagnostico: readonly OpcionMarca[] = [
    { valor: 'PRE', texto: t('formularios.f028b.marcaDiagnostico.pre') },
    { valor: 'DEF', texto: t('formularios.f028b.marcaDiagnostico.def') },
  ];

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const pares: Par[] = [
        {
          pregunta: t('formularios.f028b.constantes.titulo'),
          respuesta: (Object.keys(CONSTANTES_VACIAS) as (keyof ConstantesVitales)[])
            .filter((c) => constantes[c])
            .map((c) => `${t(`formularios.f028b.constantes.${c}`)}: ${constantes[c]}`)
            .join(' | '),
        },
        { pregunta: t('formularios.f028b.informante'), respuesta: informante.trim() },
        { pregunta: t('formularios.f028b.motivoConsulta'), respuesta: tipoConsulta ? t(`formularios.f028b.tipoConsultaOpciones.${tipoConsulta}`) : '' },
        { pregunta: t('formularios.f028b.motivoConsultaDetalle'), respuesta: motivoConsultaTexto.trim() },
        { pregunta: t('formularios.f028b.enfermedadActual'), respuesta: enfermedadActual.trim() },
        { pregunta: t('formularios.f028b.antecedentesPersonales'), respuesta: antecedentesPersonales.trim() },
        { pregunta: t('formularios.f028b.antecedentesFamiliares'), respuesta: antecedentesFamiliares.trim() },
        {
          pregunta: t('formularios.f028b.seccionAiepi'),
          respuesta: AIEPI_2M_5A.filter((c) => aiepi[c]).map((c) => t(`formularios.f028b.aiepi.${c}`)).join(', '),
        },
        {
          pregunta: t('formularios.f028b.seccionExamenFisico'),
          respuesta: EXAMEN_FISICO_SISTEMAS.filter((c) => examenFisico[c]).map((c) => t(`formularios.f028b.examenFisico.${c}`)).join(', '),
        },
        { pregunta: t('formularios.f028b.descripcionExamenFisico'), respuesta: descripcionExamenFisico.trim() },
        { pregunta: t('formularios.f028b.analisisCaso'), respuesta: analisisCaso.trim() },
        { pregunta: t('formularios.f028b.clasificacionAiepi'), respuesta: clasificacionAiepi.trim() },
        ...diagnosticos
          .map((dx, i) => ({ pregunta: `${t('formularios.f028b.diagnostico')} ${i + 1}`, respuesta: textoDiagnosticoConMarca(dx, marcasDiagnostico) }))
          .filter((p) => p.respuesta.trim() !== ''),
        { pregunta: t('formularios.f028b.planTratamiento'), respuesta: planTratamiento.trim() },
        { pregunta: t('formularios.f028b.proximaCitaIdeal'), respuesta: proximaCitaIdeal },
      ].filter((p) => p.respuesta.trim() !== '');

      const lote = new LoteTransaccional();
      for (const dx of diagnosticos) {
        if (dx.diagnostico.texto.trim()) {
          lote.crear({ resourceType: 'Condition', subject, encounter, code: codeDeDiagnostico(dx.diagnostico), recordedDate: new Date().toISOString() });
        }
      }

      await lote.enviar(medplum);
      onGuardado(pares, datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f028b.constantes.titulo')}</h4>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {(Object.keys(CONSTANTES_VACIAS) as (keyof ConstantesVitales)[]).map((campo) => (
          <CampoTexto key={campo} label={t(`formularios.f028b.constantes.${campo}`)} value={constantes[campo]} onChange={(v) => setConstantes((prev) => ({ ...prev, [campo]: v }))} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f028b.motivoConsulta')}</Label>
          <Select value={tipoConsulta || undefined} onValueChange={setTipoConsulta}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIPO_CONSULTA_OPCIONES.map((op) => (
                <SelectItem key={op} value={op}>
                  {t(`formularios.f028b.tipoConsultaOpciones.${op}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <CampoTexto label={t('formularios.f028b.informante')} value={informante} onChange={setInformante} />
      </div>
      <AreaDictado id="f028b-motivo-consulta-detalle" etiqueta={t('formularios.f028b.motivoConsultaDetalle')} valor={motivoConsultaTexto} onChange={setMotivoConsultaTexto} filas={2} />

      <AreaDictado id="f028b-enfermedad-actual" etiqueta={t('formularios.f028b.enfermedadActual')} valor={enfermedadActual} onChange={setEnfermedadActual} filas={2} />

      <AreaDictado id="f028b-antecedentes-personales" etiqueta={t('formularios.f028b.antecedentesPersonales')} valor={antecedentesPersonales} onChange={setAntecedentesPersonales} filas={2} />
      <AreaDictado id="f028b-antecedentes-familiares" etiqueta={t('formularios.f028b.antecedentesFamiliares')} valor={antecedentesFamiliares} onChange={setAntecedentesFamiliares} filas={2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f028b.seccionAiepi')}</h4>
      <ChecklistGrid claves={AIEPI_2M_5A} prefijoI18n="formularios.f028b.aiepi" prefijoId="aiepi" valores={aiepi} onChange={setAiepi} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f028b.seccionExamenFisico')}</h4>
      <ChecklistGrid claves={EXAMEN_FISICO_SISTEMAS} prefijoI18n="formularios.f028b.examenFisico" prefijoId="examen" valores={examenFisico} onChange={setExamenFisico} />
      <AreaDictado id="f028b-descripcion-examen-fisico" etiqueta={t('formularios.f028b.descripcionExamenFisico')} valor={descripcionExamenFisico} onChange={setDescripcionExamenFisico} filas={2} />

      <AreaDictado id="f028b-analisis-caso" etiqueta={t('formularios.f028b.analisisCaso')} valor={analisisCaso} onChange={setAnalisisCaso} filas={2} />
      <AreaDictado id="f028b-clasificacion-aiepi" etiqueta={t('formularios.f028b.clasificacionAiepi')} valor={clasificacionAiepi} onChange={setClasificacionAiepi} filas={2} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f028b.seccionDiagnosticos')}</h4>
      <div className="flex flex-col gap-2">
        {diagnosticos.map((dx, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f028b.diagnostico')} ${i + 1}`}
            marcaLabel={t('formularios.f028b.marcaDiagnostico.label')}
            marcas={marcasDiagnostico}
            value={dx}
            onChange={(nuevo) => setDiagnosticos((prev) => prev.map((d, idx) => (idx === i ? nuevo : d)))}
          />
        ))}
      </div>

      <AreaDictado id="f028b-plan-tratamiento" etiqueta={t('formularios.f028b.planTratamiento')} valor={planTratamiento} onChange={setPlanTratamiento} filas={2} />
      <div className="flex flex-col gap-1">
        <Label>{t('formularios.f028b.proximaCitaIdeal')}</Label>
        <Input type="date" value={proximaCitaIdeal} onChange={(e) => setProximaCitaIdeal(e.target.value)} />
      </div>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
